import React, { useEffect, useRef, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Masonry from 'masonry-layout';
import imagesLoaded from 'imagesloaded';
/* TODO: переделать на module.css */
import './W_MasonryGrid.css';

interface W_MasonryGridProps {
  children: ReactNode;
  className?: string;
  itemClassName?: string;
  gutter?: number;
  columnWidth?: string;
  threeColumns?: boolean;
  fourColumns?: boolean;
}

const LAYOUT_DELAYS = [50, 200, 500, 1000];

/* TODO: убрать threeColumns и fourColumns */
export const W_MasonryGrid: React.FC<W_MasonryGridProps> = ({
  children,
  className = 'masonry-grid',
  itemClassName = 'masonry-item',
  gutter = 20,
  columnWidth = '.masonry-item',
  threeColumns = false,
  fourColumns = false,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const masonryRef = useRef<Masonry | null>(null);
  const location = useLocation();

  const updateMasonry = useCallback(() => {
    if (masonryRef.current) {
      (masonryRef.current as any).reloadItems?.();
      (masonryRef.current as any).layout?.();
    }
  }, []);

  const updateMasonryAfterImagesLoad = useCallback(() => {
    if (!gridRef.current || !masonryRef.current) return;
    imagesLoaded(gridRef.current, () => {
      updateMasonry();
    });
  }, [updateMasonry]);

  useEffect(() => {
    if (gridRef.current && !masonryRef.current) {
      masonryRef.current = new Masonry(gridRef.current, {
        itemSelector: `.${itemClassName}`,
        columnWidth,
        percentPosition: true,
        gutter,
      });
    }
  }, [itemClassName, columnWidth, gutter]);

  useEffect(() => {
    if (!masonryRef.current || !gridRef.current) return;

    LAYOUT_DELAYS.forEach(delay =>
      setTimeout(updateMasonryAfterImagesLoad, delay)
    );

    const items = gridRef.current.querySelectorAll(`.${itemClassName}`);
    let rafId = 0;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateMasonry();
      });
    });
    items.forEach(item => resizeObserver.observe(item));

    const images = gridRef.current.querySelectorAll('img');
    const onImageLoad = () => requestAnimationFrame(updateMasonry);
    const cleanupImages: Array<{ img: HTMLImageElement; fn: () => void }> = [];
    images.forEach(img => {
      if (img.complete) onImageLoad();
      else {
        img.addEventListener('load', onImageLoad);
        cleanupImages.push({ img, fn: onImageLoad });
      }
    });

    return () => {
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      cleanupImages.forEach(({ img, fn }) =>
        img.removeEventListener('load', fn)
      );
    };
  }, [children, updateMasonry, updateMasonryAfterImagesLoad, itemClassName]);

  useEffect(() => {
    const timer = setTimeout(updateMasonryAfterImagesLoad, 150);
    return () => clearTimeout(timer);
  }, [location.pathname, updateMasonryAfterImagesLoad]);

  useEffect(() => {
    const handleResize = () => requestAnimationFrame(updateMasonry);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateMasonry]);

  let gridClassName = className;
  if (threeColumns) {
    gridClassName = `${className} masonry-grid--three-columns`;
  } else if (fourColumns) {
    gridClassName = `${className} masonry-grid--four-columns`;
  }

  return (
    <div ref={gridRef} className={gridClassName}>
      {React.Children.map(children, (child, index) => (
        <div key={index} className={itemClassName}>
          {child}
        </div>
      ))}
    </div>
  );
};
